import { useEffect, useRef, useState } from "react"
import useSnackBarContext from "@contexts/snackBarContext"
import useFileUpload from "@hooks/useFileUpload"
import { useLocation } from 'react-router-dom'
import { colors, size } from "@styles/theme"
import getCroppedImg from "@utils/cropImage"
import useLoading from "@hooks/useLoading"
import styled from "@emotion/styled"

import { Button, CircularProgress, Dialog, LinearProgress } from "@mui/material"
import { PiArrowArcLeftBold, PiFloppyDiskBold } from "react-icons/pi"
import Cropper from "react-easy-crop"

const HeaderText = styled.h2`
  position: absolute;
  left: ${size(2)};
  top: 0;
  color: white;
  z-index: 1;
`

const MediaProgressIndicator = styled(CircularProgress)`
  position: absolute;
  top: ${size(2)};
  right: ${size(2)};
  z-index: 1;
`

const ButtonArea = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: ${size(2)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const ActionButton = styled(Button)`
  color: white;
  background-color: ${colors.statusColors.right_now('primary')};
  border-radius: 100px;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  height: 100vh;
  width: 100vw;
  padding: 0 2rem 4rem 2rem;

  z-index: 2;
  background-color: black;
`

const LoadingText = styled.h3`
  display: flex;
  align-items: center;
  text-align: center;
`

const ImageCropperDialiog = ({ isOpen, setClosed, fileUrl, uploadImage, headerText='Select Thumbnail', aspect=(1/1) }) => {
  const location = useLocation()
  const passedAspect = location?.state?.aspect
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const { addSnackBar, snackError } = useSnackBarContext()
  const { isLoading: mediaIsLoading, isLoadingDelayed: isMediaDelayed, setIsLoading: setMediaLoading } = useLoading(true)
  const { isLoading, isLoadingDelayed, setIsLoading } = useLoading()
  const loadingFailureTimeoutRef = useRef(null)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const {
    resetFileState,
    fileUrl: croppedFileUrl,
    setFileUrl: setCroppedFileUrl,
  } = useFileUpload()

  // Resets the initial state every time the modal closes
  useEffect(() => {
    if(!isOpen) setTimeout(() => resetData(), 300)
  }, [isOpen])

  // Starts timeout for potential image loading failure.
  useEffect(() => {
    if(isOpen && mediaIsLoading) {
      loadingFailureTimeoutRef.current = setTimeout(() => {
        snackError({ error: 'Could not process image. Please try another.', })
        resetData()
        setClosed()
      }, 10_000)
    }
  }, [isOpen])

  const resetData = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    resetFileState()
    setMediaLoading(true)
  }

  const resetCropState = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const showCroppedImage = async () => {
    setIsLoading(true)
    try {
      const [croppedFile, url] = await getCroppedImg(
        fileUrl,
        croppedAreaPixels,
      )
      setCroppedFileUrl(url)
      uploadImage(croppedFile).finally(() => {
        resetFileState()
        setIsLoading(false)
      })
    } catch (e) {
      console.error(e)
    }
  }

  const handleMediaLoad = () => {
    clearTimeout(loadingFailureTimeoutRef.current)
    loadingFailureTimeoutRef.current = null
    setMediaLoading(false)
  }

  return (
    <Dialog
      slotProps={{
        backdrop: { style: { backgroundColor: 'black', padding: 0 } },
        paper: {
          style: {
            width: '100dvw',
            maxWidth: '100dvw',
            height: '100dvh',
            maxHeight: '100dvh',
            position: 'relative',
            backgroundColor: 'transparent',
            borderRadius: '0px',
            margin: 0,
            overflow: 'auto',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }
        }
      }}
      open={isOpen}
      onClose={setClosed}>
      {mediaIsLoading && <MediaProgressIndicator thickness={8} />}
      <HeaderText>{headerText}</HeaderText>
      <Cropper
        image={fileUrl}
        crop={crop}
        zoom={zoom}
        aspect={passedAspect || aspect}
        objectFit='contain'
        onCropChange={setCrop}
        onCropComplete={onCropComplete}
        onZoomChange={setZoom}
        onMediaLoaded={handleMediaLoad}
        // rotation={rotation}
        // onRotationChange={setRotation}
      />
      <ButtonArea>
        <ActionButton
          onClick={setClosed}
          size='large'
          style={{ backgroundColor: colors.statusColors.right_now('light') }}>
          Cancel
        </ActionButton>
        <div>
          <ActionButton
            onClick={resetCropState}
            size='large'
            style={{ marginRight: size(2) }}>
            <PiArrowArcLeftBold
              size={size(2)}
              style={{ marginRight: size(0.25), marginTop: '-1px' }}
            />
            Reset
          </ActionButton>
          <ActionButton
            onClick={showCroppedImage}
            size='large'>
            <PiFloppyDiskBold
              size={size(2)}
              style={{ marginRight: size(0.25), marginTop: '-1px' }}
            />
            Save
          </ActionButton>
        </div>
      </ButtonArea>
      {isLoading &&
        <LoadingContainer>
          <LoadingText>Saving Image</LoadingText>
          <br />
          <LinearProgress style={{ width: `calc(100% - ${size(6)})` }} />
        </LoadingContainer>
      }
    </Dialog>
  )
}

export default ImageCropperDialiog
